import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  ClipboardList,
  LogOut,
  User,
  ChevronDown,
  Activity,
  Loader2,
  CheckCircle2,
  Clock,
  FileText,
  PlayCircle,
  LayoutGrid,
  BarChart3,
  Target,
  TrendingUp
} from 'lucide-react';
import { authAPI } from '../services/api';
import { MobileNavDoctor } from '../components/MobileNav';

const DoctorDashboardPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [surveysData, setSurveysData] = useState({ surveys: [], total: 0, completed: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [showMySurveys, setShowMySurveys] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      
      // Redirect admins to main dashboard
      if (userData.role === 'administrator') {
        navigate('/dashboard');
        return;
      }
      
      const surveys = await authAPI.getMySurveys();
      setSurveysData(surveys);
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 401) navigate('/auth');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await authAPI.logout();
    navigate('/auth');
  };

  const getTypeColor = (type) => {
    const colors = {
      kap: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      mrs: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      patient: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
      hcp: 'bg-amber-500/10 text-amber-400 border-amber-500/20'
    };
    return colors[type] || 'bg-slate-500/10 text-slate-400';
  };

  const getTypeName = (type) => {
    const names = { kap: 'KAP Survey', mrs: 'Market Research', patient: 'Patient Survey', hcp: 'HCP Feedback' };
    return names[type] || type;
  };

  const getRoleName = (role) => {
    const names = { doctor: 'Doctor', sponsor: 'Sponsor', agency: 'Agency' };
    return names[role] || role;
  };

  // Format doctor name with Dr. prefix
  const formatDoctorName = (name) => {
    if (!name) return 'Doctor';
    // Check if name already starts with Dr.
    if (name.toLowerCase().startsWith('dr.') || name.toLowerCase().startsWith('dr ')) {
      return name;
    }
    return `Dr. ${name}`;
  };

  const completionRate = surveysData.total > 0 ? Math.round((surveysData.completed / surveysData.total) * 100) : 0;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-cyan-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-slate-900 border-r border-slate-800 z-40 hidden lg:flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">KAP<span className="text-cyan-400">Surv</span></span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          <button 
            onClick={() => setShowMySurveys(false)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors ${
              !showMySurveys ? 'text-white bg-white/10' : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <LayoutGrid className="w-5 h-5" />
            My Dashboard
          </button>
          <button 
            onClick={() => setShowMySurveys(true)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
              showMySurveys ? 'text-white bg-white/10 font-medium' : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <ClipboardList className="w-5 h-5" />
            My Surveys
            {surveysData.total > 0 && (
              <Badge className="ml-auto bg-cyan-500/20 text-cyan-400 text-xs">{surveysData.total}</Badge>
            )}
          </button>
        </nav>

        {/* User Profile */}
        <div className="p-4 border-t border-slate-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-white font-semibold">
                  {user?.name?.charAt(0) || 'D'}
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-white truncate">{formatDoctorName(user?.name)}</p>
                  <p className="text-xs text-slate-500 truncate capitalize">{getRoleName(user?.role)}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-slate-900 border-slate-800">
              <DropdownMenuItem className="gap-2 text-slate-300">
                <User className="w-4 h-4" /> Profile
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-800" />
              <DropdownMenuItem className="gap-2 text-red-400" onClick={handleLogout}>
                <LogOut className="w-4 h-4" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64 p-6">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">KAP<span className="text-cyan-400">Surv</span></span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-white">
                {user?.name?.charAt(0) || 'U'}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-slate-900 border-slate-800">
              <DropdownMenuItem className="gap-2 text-slate-300">
                <User className="w-4 h-4" /> Profile
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-800" />
              <DropdownMenuItem className="gap-2 text-red-400" onClick={handleLogout}>
                <LogOut className="w-4 h-4" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Welcome Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-white">
                Welcome back, {formatDoctorName(user?.name)}!
              </h1>
              <p className="text-slate-400 mt-1">
                {showMySurveys ? 'All your assigned surveys' : "Here's an overview of your survey progress"}
              </p>
            </div>
            <Badge className="hidden sm:flex bg-gradient-to-r from-blue-500/10 to-cyan-400/10 text-cyan-400 border-cyan-500/20">
              {getRoleName(user?.role)}
            </Badge>
          </div>
        </div>

        {/* Conditional Content based on showMySurveys */}
        {!showMySurveys ? (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Total Surveys</p>
                      <p className="text-3xl font-bold text-white mt-1">{surveysData.total}</p>
                    </div>
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500/20 to-cyan-400/20 flex items-center justify-center">
                      <ClipboardList className="w-6 h-6 text-cyan-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Completed</p>
                      <p className="text-3xl font-bold text-emerald-400 mt-1">{surveysData.completed}</p>
                    </div>
                    <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Pending</p>
                      <p className="text-3xl font-bold text-amber-400 mt-1">{surveysData.total - surveysData.completed}</p>
                    </div>
                    <div className="w-12 h-12 rounded-2xl bg-amber-500/20 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-amber-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Completion Rate</p>
                      <p className="text-3xl font-bold text-purple-400 mt-1">{completionRate}%</p>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-purple-500/20 flex items-center justify-center">
                  <Target className="w-6 h-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progress Overview */}
        <Card className="bg-slate-900 border-slate-800 mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-cyan-400" /> Your Progress
                </CardTitle>
                <CardDescription className="text-slate-400">Overall survey completion progress</CardDescription>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-white">{completionRate}%</p>
                <p className="text-sm text-slate-500">{surveysData.completed} of {surveysData.total} completed</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Progress value={completionRate} className="h-4 bg-slate-800" />
          </CardContent>
        </Card>

        {/* Surveys List */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white">Your Assigned Surveys</CardTitle>
            <CardDescription className="text-slate-400">Click on a survey to start or view details</CardDescription>
          </CardHeader>
          <CardContent>
            {surveysData.surveys.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardList className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                <p className="text-slate-500">No surveys assigned to you yet.</p>
                <p className="text-slate-600 text-sm mt-2">Please wait for the administrator to assign surveys.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {surveysData.surveys.map((survey) => (
                  <div
                    key={survey.id}
                    className={`p-6 rounded-xl border transition-all ${
                      survey.completed 
                        ? 'bg-emerald-500/5 border-emerald-500/20 hover:border-emerald-500/40' 
                        : 'bg-slate-800/50 border-slate-700 hover:border-cyan-500/40 cursor-pointer'
                    }`}
                    onClick={() => {
                      if (!survey.completed) {
                        navigate(`/surveys/${survey.id}/respond`);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h3 className="text-lg font-semibold text-white">{survey.title}</h3>
                          <Badge variant="outline" className={getTypeColor(survey.type)}>
                            {getTypeName(survey.type)}
                          </Badge>
                          {survey.completed ? (
                            <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Completed
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">
                              <Clock className="w-3 h-3 mr-1" /> Pending
                            </Badge>
                          )}
                        </div>
                        <p className="text-slate-400 mb-3">{survey.description || 'No description available'}</p>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <FileText className="w-4 h-4" /> {survey.questions_count} questions
                          </span>
                          {survey.has_consent && (
                            <span className="text-cyan-400">• Consent required</span>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        {survey.completed ? (
                          <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center">
                            <CheckCircle2 className="w-7 h-7 text-emerald-400" />
                          </div>
                        ) : (
                          <Button 
                            className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/surveys/${survey.id}/respond`);
                            }}
                          >
                            <PlayCircle className="w-4 h-4 mr-2" /> Start Survey
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
          </>
        ) : (
          /* My Surveys List View */
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white">All Assigned Surveys</CardTitle>
              <CardDescription className="text-slate-400">
                {surveysData.surveys.length} survey{surveysData.surveys.length !== 1 ? 's' : ''} assigned to you
              </CardDescription>
            </CardHeader>
            <CardContent>
              {surveysData.surveys.length === 0 ? (
                <div className="text-center py-12">
                  <ClipboardList className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                  <p className="text-slate-500">No surveys assigned to you yet.</p>
                  <p className="text-slate-600 text-sm mt-2">Please wait for the administrator to assign surveys.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {surveysData.surveys.map((survey) => (
                    <div
                      key={survey.id}
                      className={`p-6 rounded-xl border transition-all ${
                        survey.completed 
                          ? 'bg-emerald-500/5 border-emerald-500/20 hover:border-emerald-500/40' 
                          : 'bg-slate-800/50 border-slate-700 hover:border-cyan-500/40 cursor-pointer'
                      }`}
                      onClick={() => {
                        if (!survey.completed) {
                          navigate(`/surveys/${survey.id}/respond`);
                        }
                      }}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="text-lg font-semibold text-white">{survey.title}</h3>
                            <Badge variant="outline" className={getTypeColor(survey.type)}>
                              {getTypeName(survey.type)}
                            </Badge>
                            {survey.completed ? (
                              <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Completed
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">
                                <Clock className="w-3 h-3 mr-1" /> Pending
                              </Badge>
                            )}
                          </div>
                          <p className="text-slate-400 mb-3">{survey.description || 'No description available'}</p>
                          <div className="flex items-center gap-4 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <FileText className="w-4 h-4" /> {survey.questions_count} questions
                            </span>
                            {survey.has_consent && (
                              <span className="text-cyan-400">• Consent required</span>
                            )}
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          {survey.completed ? (
                            <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center">
                              <CheckCircle2 className="w-7 h-7 text-emerald-400" />
                            </div>
                          ) : (
                            <Button 
                              className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/surveys/${survey.id}/respond`);
                              }}
                            >
                              <PlayCircle className="w-4 h-4 mr-2" /> Start Survey
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileNavDoctor />
      
      {/* Bottom Padding for Mobile Nav */}
      <div className="lg:hidden h-24" />
    </div>
  );
};

export default DoctorDashboardPage;
